import { memo, useCallback } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { initialFleetVehicleDriverSearchForm } from "services/api/fleet/vehicles/drivers/consts";
import { DriverSourceEnum } from "services/api/fleet/vehicles/drivers/types";

// UI
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import VehicleDropdown from "ui/components/Dropdown/VehicleDropdown/VehicleDropdown";
import { TextField } from "ui/components/TextField/TextField";

import {
  DRIVER_EXPIRY_FILTER_OPTIONS,
  DRIVER_SOURCE_FILTER_OPTIONS,
} from "../../consts";
import { DriverSearchFiltersProps } from "./types";

/** Kraj dana zadanog datuma — rok isteka se gleda do kraja tog dana. */
const endOfDay = (date: Date): Date => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);

  return end;
};

const DriverSearchFilters: React.FC<DriverSearchFiltersProps> = (props) => {
  const { formValues, onSearchChange } = props;

  const { t } = useTranslate();

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onSearchChange({ ...formValues, search: e.target.value });
    },
    [formValues, onSearchChange]
  );

  const handleVehicleChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({ ...formValues, vehicleId: item.value || null });
    },
    [formValues, onSearchChange]
  );

  const handleSourceChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        source: (item.value as DriverSourceEnum) || null,
      });
    },
    [formValues, onSearchChange]
  );

  // Prečac se pretvara u raspon: istekle = sve do danas, "u N dana" = od danas do +N.
  const handleExpiryChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      const option = DRIVER_EXPIRY_FILTER_OPTIONS.find(
        (o) => o.value === item.value
      );

      if (!option) {
        onSearchChange({ ...formValues, expiryFrom: null, expiryTo: null });
        return;
      }

      const today = new Date();

      if (option.days === null) {
        onSearchChange({
          ...formValues,
          expiryFrom: null,
          expiryTo: endOfDay(today),
        });
        return;
      }

      const until = new Date(today);
      until.setDate(until.getDate() + option.days);

      onSearchChange({
        ...formValues,
        expiryFrom: today,
        expiryTo: endOfDay(until),
      });
    },
    [formValues, onSearchChange]
  );

  const handleResetFormClick = useCallback(() => {
    onSearchChange(initialFleetVehicleDriverSearchForm);
  }, [onSearchChange]);

  // Odabrani prečac se prepoznaje po tome je li raspon otvoren prema prošlosti ili budućnosti.
  const expiryValue = !formValues.expiryTo
    ? null
    : formValues.expiryFrom
      ? String(
          Math.floor(
            (formValues.expiryTo.getTime() - formValues.expiryFrom.getTime()) /
              (24 * 60 * 60 * 1000)
          )
        )
      : "expired";

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[240px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleSearchChange}
          placeholder="Ime vozača..."
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      {/* Naziv vozila je dug (marka, model, tip), pa polje uzima dvostruku širinu.
          Filter samo bira postojeće vozilo — unos novog ovdje nema smisla. */}
      <div className="min-w-[440px]">
        <VehicleDropdown
          name="vehicleId"
          placeholder="Vozilo"
          value={formValues.vehicleId}
          onChange={handleVehicleChange}
          showAddNew={false}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="min-w-[190px]">
        <Dropdown
          name="source"
          placeholder="Vrsta vozača"
          options={DRIVER_SOURCE_FILTER_OPTIONS}
          value={formValues.source}
          onChange={handleSourceChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="min-w-[190px]">
        <Dropdown
          name="expiry"
          placeholder="Rokovi isprava"
          options={DRIVER_EXPIRY_FILTER_OPTIONS}
          value={expiryValue}
          onChange={handleExpiryChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="flex items-center pb-2">
        <span
          onClick={handleResetFormClick}
          className="text-xs font-bold cursor-pointer text-gray-700"
        >
          {t("admin.reset")}
        </span>
      </div>
    </div>
  );
};

export default memo(DriverSearchFilters);
